import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { useToast } from '@/hooks/use-toast'
import {
  listRegistrations,
  sendZoomLinks,
  webinarStorage,
  type WebinarRegistration,
} from '@/services/webinarApi'

const formatDate = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function WebinarAdminDashboard() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { toast } = useToast()
  const [confirmingSend, setConfirmingSend] = useState(false)
  const [excludedIds, setExcludedIds] = useState<Set<string>>(new Set())

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['webinar-registrations'],
    queryFn: listRegistrations,
    retry: 1,
  })

  // IDs of registrants without a sent link (the only ones that have a checkbox)
  const pendingIds = useMemo(
    () =>
      (data?.items ?? [])
        .filter((r) => !r.reminder_sent_at)
        .map((r) => r.id),
    [data],
  )

  // Drop any excluded IDs that are no longer in the pending list (e.g. after a refresh)
  useEffect(() => {
    if (excludedIds.size === 0) return
    const pendingSet = new Set(pendingIds)
    const next = new Set([...excludedIds].filter((id) => pendingSet.has(id)))
    if (next.size !== excludedIds.size) setExcludedIds(next)
  }, [pendingIds, excludedIds])

  const selectedIds = useMemo(
    () => pendingIds.filter((id) => !excludedIds.has(id)),
    [pendingIds, excludedIds],
  )
  const selectedCount = selectedIds.length

  const toggleRow = (id: string) => {
    setExcludedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleAll = (checked: boolean) => {
    setExcludedIds(checked ? new Set() : new Set(pendingIds))
  }

  const sendMutation = useMutation({
    mutationFn: () => sendZoomLinks({ ids: selectedIds }),
    onSuccess: (result) => {
      const summary = result.message
        ? result.message
        : `${result.sent} email(s) envoyé(s), ${result.failed} échec(s).`
      toast({
        title: 'Envoi terminé',
        description: summary,
        variant: result.failed > 0 ? 'destructive' : undefined,
      })
      queryClient.invalidateQueries({ queryKey: ['webinar-registrations'] })
      setConfirmingSend(false)
    },
    onError: (err: any) => {
      const detail =
        err?.response?.data?.detail ?? "Échec de l'envoi. Vérifiez la configuration Zoom."
      toast({
        title: "Erreur",
        description: detail,
        variant: 'destructive',
      })
      setConfirmingSend(false)
    },
  })

  const handleLogout = () => {
    webinarStorage.clearKey()
    navigate('/inscriptions-admin/login')
  }

  const total = data?.total ?? 0
  const sentTotal = data?.reminder_sent_total ?? 0
  const pending = total - sentTotal
  const zoomConfigured = data?.zoom_url_configured ?? false

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-bronze shadow-md">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-xl font-bold text-white">Inscriptions Webinaire</h1>
            <p className="text-white/70 text-sm">MAIN × LinkAfric · PI-SPI</p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            className="border-white text-white bg-transparent hover:bg-white/20 hover:text-white"
          >
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        {/* Stats + actions */}
        <div className="grid sm:grid-cols-3 gap-4 mb-6">
          <StatCard label="Total inscrits" value={total} />
          <StatCard label="Lien Zoom envoyé" value={sentTotal} accent="success" />
          <StatCard label="En attente d'envoi" value={pending} accent="warning" />
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-border p-5 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="font-semibold text-bronze">Envoi du lien Zoom</h2>
              <p className="text-sm text-muted-foreground">
                {zoomConfigured
                  ? excludedIds.size > 0
                    ? `${selectedCount} sélectionné(s) sur ${pending} en attente · ${excludedIds.size} ignoré(s).`
                    : `À déclencher idéalement le 12 mai 2026 (J-1). ${pending} inscrit(s) en attente.`
                  : "URL Zoom non configurée. Définir WEBINAR_ZOOM_URL dans .env.prod et redémarrer le backend."}
              </p>
            </div>
            <div className="flex gap-2">
              {confirmingSend ? (
                <>
                  <Button
                    onClick={() => sendMutation.mutate()}
                    disabled={sendMutation.isPending || !zoomConfigured || selectedCount === 0}
                    className="bg-gold hover:bg-gold/90 text-white"
                  >
                    {sendMutation.isPending ? 'Envoi…' : `Confirmer (${selectedCount})`}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setConfirmingSend(false)}
                    disabled={sendMutation.isPending}
                  >
                    Annuler
                  </Button>
                </>
              ) : (
                <Button
                  onClick={() => setConfirmingSend(true)}
                  disabled={!zoomConfigured || selectedCount === 0}
                  className="bg-gold hover:bg-gold/90 text-white"
                >
                  Envoyer le lien Zoom ({selectedCount})
                </Button>
              )}
            </div>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border border-border overflow-hidden">
          <div className="bg-secondary/50 px-6 py-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-semibold text-bronze">Liste des inscrits</h2>
            <Button variant="outline" size="sm" onClick={() => refetch()}>
              Rafraîchir
            </Button>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-secondary/30">
                <tr>
                  <th className="px-6 py-3 w-12">
                    <input
                      type="checkbox"
                      aria-label="Tout sélectionner / désélectionner"
                      className="h-4 w-4 cursor-pointer accent-gold disabled:opacity-30"
                      disabled={pendingIds.length === 0}
                      checked={pendingIds.length > 0 && selectedCount === pendingIds.length}
                      ref={(el) => {
                        if (el) el.indeterminate = selectedCount > 0 && selectedCount < pendingIds.length
                      }}
                      onChange={(e) => toggleAll(e.target.checked)}
                    />
                  </th>
                  <Th>Institution</Th>
                  <Th>Contact</Th>
                  <Th>Pays</Th>
                  <Th>Inscrit le</Th>
                  <Th>Lien Zoom</Th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-border">
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <div className="flex items-center justify-center gap-2">
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gold" />
                        <span className="text-muted-foreground">Chargement…</span>
                      </div>
                    </td>
                  </tr>
                ) : isError ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center">
                      <p className="text-destructive mb-2">Erreur de chargement</p>
                      <Button variant="outline" size="sm" onClick={() => refetch()}>
                        Réessayer
                      </Button>
                    </td>
                  </tr>
                ) : !data?.items?.length ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-8 text-center text-muted-foreground">
                      Aucune inscription pour le moment.
                    </td>
                  </tr>
                ) : (
                  data.items.map((reg: WebinarRegistration) => {
                    const isPending = !reg.reminder_sent_at
                    const isExcluded = excludedIds.has(reg.id)
                    return (
                    <tr
                      key={reg.id}
                      className={`hover:bg-secondary/20 transition-colors ${
                        isPending && isExcluded ? 'opacity-50' : ''
                      }`}
                    >
                      <td className="px-6 py-3">
                        {isPending ? (
                          <input
                            type="checkbox"
                            aria-label={`Inclure ${reg.institution} dans l'envoi`}
                            className="h-4 w-4 cursor-pointer accent-gold"
                            checked={!isExcluded}
                            onChange={() => toggleRow(reg.id)}
                          />
                        ) : (
                          <span className="text-muted-foreground/40 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-6 py-3">
                        <div className="font-medium text-foreground">{reg.institution}</div>
                      </td>
                      <td className="px-6 py-3">
                        <div className="text-foreground">{reg.nom}</div>
                        <div className="text-xs text-muted-foreground">{reg.fonction}</div>
                        <div className="text-xs text-muted-foreground">
                          <a href={`mailto:${reg.email}`} className="hover:text-bronze">
                            {reg.email}
                          </a>
                          {' · '}
                          {reg.telephone}
                        </div>
                      </td>
                      <td className="px-6 py-3 text-muted-foreground">{reg.pays}</td>
                      <td className="px-6 py-3 text-muted-foreground text-sm">
                        {formatDate(reg.created_at)}
                      </td>
                      <td className="px-6 py-3">
                        {reg.reminder_sent_at ? (
                          <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-green-50 text-green-700 border border-green-200">
                            ✓ Envoyé · {formatDate(reg.reminder_sent_at)}
                          </span>
                        ) : isExcluded ? (
                          <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-muted text-muted-foreground border border-border">
                            Ignoré
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-xs px-2 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                            En attente
                          </span>
                        )}
                      </td>
                    </tr>
                  )})
                )}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
      {children}
    </th>
  )
}

function StatCard({
  label,
  value,
  accent,
}: {
  label: string
  value: number
  accent?: 'success' | 'warning'
}) {
  const tone =
    accent === 'success'
      ? 'text-green-700'
      : accent === 'warning'
        ? 'text-amber-700'
        : 'text-bronze'
  return (
    <div className="bg-white rounded-lg shadow-sm border border-border p-5">
      <div className="text-sm text-muted-foreground">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${tone}`}>{value}</div>
    </div>
  )
}
